import { memo } from 'react'
import { useTenantUserEntryDialog } from './useTenantUserEntryDialog'
import { Modal } from 'components/layouts/modal/modal'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { InviteNewUsersToolbar } from './component/inviteNewUsersToolbar/inviteNewUsersToolbar'
import { InviteNewUserListPanel } from './component/inviteNewUserListPanel/inviteNewUserListPanel'
import { CrewErrorBoundary } from 'components/functions/crewErrorBoundary'
import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { useTranslation } from '@crew/modules/i18n'
import { useCallback, useEffect, useState } from 'react'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { useGetTenantSettingsQuery } from '@crew/apis/setting/settingApis'
import { GetTenantSettingsRequest } from '@crew/apis/setting/models/getTenantSettings/request'
import {
  ContractPlan,
  InvitationRoleType,
  SettingKeyType,
} from '@crew/enums/app'
import { NewUserType } from './useTenantUserEntryDialog'
import { useGetInviteableUsersQuery } from '@crew/apis/user/userApis'
import { useAppSelector } from 'states/hooks'
import { GetInviteableUsersResponse } from '@crew/apis/user/models/getInviteableUsers/response'

export type PropsMode = {
  title: string
  isOpen: boolean
  onClose: () => void
}

export const TenantUserEntryDialog = memo((props: PropsMode) => {
  const {
    tenantUserEntryDialogContext,
    insertUserPendings,
    isLoadingInsertUserPending,
  } = useTenantUserEntryDialog()

  const tenantSettingUserEventMessage = useAppSelector(
    (state) => state.app.tenantSettingUserEventMessage
  )

  const currentPlan = useAppSelector((state) => state.app.currentPlan)

  const { t } = useTranslation()
  const [showApiErrors] = useShowApiErrors()
  const toast = useToast()

  // 招待可能なユーザー数を取得
  const {
    data: getInviteableUsersResult,
    refetch: refetchInviteableUsersResult,
  } = useGetInviteableUsersQuery()

  // refetch inviteable users/external users when tenant setting member updated
  useEffect(() => {
    if (tenantSettingUserEventMessage) {
      refetchInviteableUsersResult()
    }
  }, [currentPlan, tenantSettingUserEventMessage, refetchInviteableUsersResult])

  const [organizationDomain, setOrganizationDomain] = useState<string | null>(
    null
  )
  const [selectedNewUsers, setSelectedNewUsers] = useState<NewUserType[]>([])

  //close dialog
  const handleCloseDialog = useCallback(() => {
    props.onClose()

    //clear context state
    setSelectedNewUsers([])
  }, [props])

  const getTenantSettingsRequestParams: GetTenantSettingsRequest = {
    keys: [SettingKeyType.OrganizationDomain],
  }
  // get tenant setting data
  const { data: getTenantSettings } = useGetTenantSettingsQuery(
    getTenantSettingsRequestParams
  )

  useEffect(() => {
    //save organization domain to local state
    if (
      getTenantSettings?.tenantSettings[0].key ===
      SettingKeyType.OrganizationDomain
    ) {
      setOrganizationDomain(getTenantSettings.tenantSettings[0].value)
    }
  }, [getTenantSettings?.tenantSettings])

  //add button click invite user to tenant
  const handleInviteMemberButtonClick = useCallback(async () => {
    //invite new user to tenant
    try {
      //if selected new users is empty, return
      if (selectedNewUsers.length === 0) return

      const externalUsersCount = selectedNewUsers.filter(
        (user) => user.invitationRoleType === InvitationRoleType.External.key
      ).length

      const internalUsersCount = selectedNewUsers.length - externalUsersCount

      const inviteableUsers =
        getInviteableUsersResult ??
        ({
          inviteableUsers: 0,
          inviteableExternalUsers: 0,
          currentExternalUsers: 0,
          currentUsers: 0,
          remainingInviteableUsers: 0,
          remainingInviteableExternalUsers: 0,
          totalRemainingInviteableUsers: 0,
        } satisfies GetInviteableUsersResponse)

      // 内部ユーザーの空き枠数が足りているか
      if (internalUsersCount > inviteableUsers.remainingInviteableUsers) {
        toast.error(
          t('message.apiError.inviteTenantUsersExceedLimit', {
            remainingUser: inviteableUsers.remainingInviteableUsers,
          })
        )
        return
      }

      // 外部ユーザーの空き枠数が足りているか
      if (
        externalUsersCount > inviteableUsers.remainingInviteableExternalUsers
      ) {
        toast.error(
          t('message.apiError.inviteExternalUserLimitExceeded', {
            inviteableExternalUserCount:
              inviteableUsers.inviteableExternalUsers,
            currentExternalUserCount: inviteableUsers.currentExternalUsers,
            exceededExternalUserCount:
              externalUsersCount -
              inviteableUsers.remainingInviteableExternalUsers,
          })
        )
        return
      }

      // 内部ユーザー・外部ユーザー共通の空き枠数が足りているか。
      // 内部ユーザー単体、外部ユーザー単体での枠数上限判定は既に行っているので、
      // ここの判定でひっかかるのは、フリープランなど、内部ユーザーと外部ユーザーの枠が合算されて上限判定される場合のみ。
      if (
        internalUsersCount + externalUsersCount >
        inviteableUsers.totalRemainingInviteableUsers
      ) {
        toast.error(
          // 内部ユーザ・外部ユーザ共通なので、両者を区別しない「ユーザー数が制限を超えた」というメッセージを表示する
          t('message.apiError.inviteTenantUsersExceedLimit', {
            remainingUser: inviteableUsers.totalRemainingInviteableUsers,
          })
        )
        return
      }

      await insertUserPendings(selectedNewUsers)

      toast.success(t('message.tenant.inviteUserSuccess'))

      //clear context state
      setSelectedNewUsers([])

      //close dialog
      props.onClose()
    } catch (error) {
      console.log(error)
      showApiErrors(error)
    }
  }, [
    getInviteableUsersResult,
    insertUserPendings,
    props,
    selectedNewUsers,
    showApiErrors,
    t,
    toast,
  ])

  return (
    <tenantUserEntryDialogContext.Provider
      value={{
        selectedNewUsers,
        setSelectedNewUsers,
        organizationDomain,
      }}
    >
      <Modal
        title={props.title}
        isOpen={props.isOpen}
        onClose={handleCloseDialog}
      >
        <div className="flex flex-col gap-y-3 h-full">
          <CrewScrollView>
            <p className="text-sm my-3">
              {t('label.inviteNewUsersDescription')}
            </p>

            <div className="flex flex-col gap-y-5">
              <CrewErrorBoundary>
                <InviteNewUsersToolbar />
              </CrewErrorBoundary>

              <CrewErrorBoundary>
                <InviteNewUserListPanel />
              </CrewErrorBoundary>
            </div>
          </CrewScrollView>

          {/* Dialog action */}
          <div className="flex  gap-x-3 items-center">
            <p className="mr-auto">
              {/* 招待可能なユーザー数 */}
              {t('label.hintForAllowInviteableUsers', {
                remaining:
                  getInviteableUsersResult?.remainingInviteableUsers ?? 0,
                total: getInviteableUsersResult?.inviteableUsers ?? 0,
              })}

              {/* 招待可能な外部ユーザー数（有償プランの場合のみ表示） */}
              {(currentPlan === ContractPlan.Professional ||
                currentPlan === ContractPlan.Standard) && (
                <>
                  <br />
                  {t('label.hintForAllowInviteableExternalUsers', {
                    remaining:
                      getInviteableUsersResult?.remainingInviteableExternalUsers ??
                      0,
                    total:
                      getInviteableUsersResult?.inviteableExternalUsers ?? 0,
                  })}
                </>
              )}
            </p>
            {/* ユーザーを招待ボタン */}
            <CrewButton
              text={t('action.inviteUser')}
              type="primary"
              onClick={handleInviteMemberButtonClick}
              disabled={isLoadingInsertUserPending}
            />

            {/* キャンセルボタン */}
            <CrewButton
              text={t('action.cancel')}
              type="normal"
              stylingMode="outlined"
              onClick={handleCloseDialog}
            />
          </div>
        </div>
      </Modal>
    </tenantUserEntryDialogContext.Provider>
  )
})
